import { useEffect, useId, useRef, useState } from "react";
import "./globalCapDetailOfferings.css";

const pad = (index) => String(index + 1).padStart(2, "0");

function useRevealOnScroll() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

export default function GlobalCapDetailOfferings({ data }) {
  const [ref, visible] = useRevealOnScroll();
  const [active, setActive] = useState(0);
  const baseId = useId();

  const current = active < data.items.length ? active : 0;

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty(
      "--mx",
      `${event.clientX - rect.left}px`
    );
    event.currentTarget.style.setProperty(
      "--my",
      `${event.clientY - rect.top}px`
    );
  };

  return (
    <section
      ref={ref}
      className={`gcdof ${visible ? "gcdof--visible" : ""}`}
    >
      <div className="gcdof__container">
        <header className="gcdof__header">
          {data.eyebrow && (
            <div className="gcdof__eyebrow">
              <span className="gcdof__eyebrow-dot" aria-hidden="true" />
              {data.eyebrow}
            </div>
          )}

          <h2 className="gcdof__title-main">{data.title}</h2>

          {data.intro && <p className="gcdof__intro">{data.intro}</p>}
        </header>

        <div className="gcdof__panels">
          {data.items.map((item, index) => {
            const isActive = current === index;
            const number = item.number ?? pad(index);
            const revealId = `${baseId}-${index}`;

            return (
              <article
                className={`gcdof__panel ${
                  isActive ? "gcdof__panel--active" : ""
                }`}
                style={{ "--i": index }}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") setActive(index);
                }}
                onPointerMove={handleMove}
                key={item.title}
              >
                <span className="gcdof__ghost" aria-hidden="true">
                  {number}
                </span>

                <div className="gcdof__top">
                  <span className="gcdof__arrow" aria-hidden="true">
                    ↗
                  </span>
                </div>

                <div className="gcdof__main">
                  <h3 className="gcdof__title">
                    <button
                      type="button"
                      className="gcdof__trigger"
                      aria-expanded={isActive}
                      aria-controls={revealId}
                      onClick={() => setActive(index)}
                      onFocus={() => setActive(index)}
                    >
                      {item.title}
                    </button>
                  </h3>

                  <div className="gcdof__reveal" id={revealId}>
                    <div className="gcdof__reveal-inner">
                      <span className="gcdof__rule" aria-hidden="true" />
                      {item.subtitle && (
                        <p className="gcdof__subtitle">{item.subtitle}</p>
                      )}
                      <p className="gcdof__description">{item.description}</p>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
